'use client';
import { useDebounceFn } from 'ahooks';
import { type FC, useState } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { ordersCreateInvoice } from '../../apis';

interface InvoiceDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  orderId: string;
}

export const InvoiceDialog: FC<InvoiceDialogProps> = ({ open, onOpenChange, orderId }) => {
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(false);

  const { run: checkEmail } = useDebounceFn(
    (email: string) => {
      if (!email) {
        setIsValid(false);
        return;
      }
      if (/^[\w.-]+@[\w-]+(\.[\w-]+)+$/.test(email)) {
        setIsValid(true);
        setErrorMsg('');
      } else {
        setIsValid(false);
        setErrorMsg('请输入正确的邮箱');
      }
    },
    { wait: 500 },
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isValid && !createInvoiceLoading) {
      handleClickIdentityNext();
    }
  };

  const { run: createInvoice, loading: createInvoiceLoading } = useFetch(
    () =>
      ordersCreateInvoice({
        body: {
          order_id: orderId,
          email_addr: email,
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message || '提交失败，请稍后再试',
          });
        } else {
          toast({
            type: 'success',
            message: '提交成功，开票邮件已发送',
          });
        }
        handleClose();
      },
      onError: (error) => {
        toast({
          type: 'warning',
          message: error.message || '提交失败，请稍后再试',
        });
      },
    },
  );

  const handleClickIdentityNext = () => {
    createInvoice();
  };

  const handleClose = () => {
    setEmail('');
    setIsValid(false);
    setErrorMsg('');
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger />
      <DialogContent className="w-[560px]">
        <DialogHeader>
          <DialogTitle>发票接收方式</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Input
            placeholder="请输入邮箱"
            className={cn(
              'h-[48px] w-full rounded-8 border border-system-white12 bg-system-white08 py-[10px] text-sm text-system-white placeholder:text-system-white64',
              errorMsg && 'border-system-error',
            )}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              checkEmail(e.target.value);
            }}
            onKeyDown={handleInputKeyDown}
            maxLength={100}
          />
          {errorMsg && <p className="text-left text-system-error text-xs">{errorMsg}</p>}
          <div className="flex items-center gap-1 text-[#666970] text-sm">
            <BaseLineExclamation className="size-4" />
            <span>推荐使用网易或QQ邮箱，避免发票邮件接收失败。</span>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="primary"
            disabled={!isValid || createInvoiceLoading}
            className={cn('w-[120px]', !isValid && 'bg-system-neutral02 text-system-white24 hover:bg-system-neutral02')}
            onClick={handleClickIdentityNext}
          >
            {createInvoiceLoading ? '提交中...' : '提交'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
